import { Boxes, CalendarClock, Award } from "lucide-react";
import { Course } from "../types";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface StatsRowProps {
  courses: Course[];
}

export const StatsRow = ({ courses }: StatsRowProps) => {
  const safeCourses = courses || [];
  // Count all published courses
  const publishedCourses = safeCourses.filter(c => c.status === 'published').length;
  const upcomingEvents = safeCourses.filter(c => 
    c.status === 'published' && 
    c.start_date && 
    new Date(c.start_date) > new Date()
  ).length;

  // Fetch average CEU hours
  const { data: averageCEUHours = 0, isLoading: isLoadingAverage } = useQuery({
    queryKey: ['average-ceu-hours'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('certificates')
        .select(`
          courses:course_id (
            hours
          )
        `);
      if (error) return 0;
      const totalCertificates = data.length;
      if (totalCertificates === 0) return 0;
      const totalHours = data.reduce((sum, cert) => sum + (cert.courses?.hours || 0), 0);
      return parseFloat((totalHours / totalCertificates).toFixed(1));
    }
  });

  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-6 w-full px-2 py-2">
        {/* Published Courses */}
        <div className="flex items-center gap-3 min-w-[180px]">
          <Boxes className="h-7 w-7 text-[#0f8f8c] bg-accent-subtle p-1 rounded-lg" />
          <div className="flex flex-col">
            <span className="text-lg font-section font-medium text-primary/80">Published Courses</span>
            <span className="text-3xl font-title font-bold text-primary">{publishedCourses}</span>
            <span className="text-base font-caption text-muted-foreground">courses</span>
          </div>
        </div>
        {/* Average CEU Hours */}
        <div className="flex items-center gap-3 min-w-[180px]">
          <Award className="h-7 w-7 text-[#0f8f8c] bg-accent-subtle p-1 rounded-lg" />
          <div className="flex flex-col">
            <span className="text-lg font-section font-medium text-primary/80">Average CEU Hours</span>
            <span className="text-3xl font-title font-bold text-primary">{isLoadingAverage ? "..." : averageCEUHours}</span>
            <span className="text-base font-caption text-muted-foreground">hours/certificate</span>
          </div>
        </div>
        {/* Upcoming Events */}
        <div className="flex items-center gap-3 min-w-[180px]">
          <CalendarClock className="h-7 w-7 text-[#0f8f8c] bg-accent-subtle p-1 rounded-lg" />
          <div className="flex flex-col">
            <span className="text-lg font-section font-medium text-primary/80">Upcoming Events</span>
            <span className="text-3xl font-title font-bold text-primary">{upcomingEvents}</span>
            <span className="text-base font-caption text-muted-foreground">events</span>
          </div>
        </div>
      </div>
    </div>
  );
}; 